import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useWebSocket } from '@/context/WebSocketContext';
import api from '@/lib/api';
import { Rocket, Upload, Link as LinkIcon, Trash, Eye, ArrowClockwise, X, Plus, CaretDown, Check, Warning, Clock, Download, Desktop, Folder } from '@phosphor-icons/react';

const ACTIONS = [
  { value: 'copy_file', label: 'Datei kopieren', desc: 'Kopiert eine Datei an den Zielort' },
  { value: 'replace_file', label: 'Datei ersetzen', desc: 'Ersetzt eine bestehende Datei' },
  { value: 'replace_folder', label: 'Ordner ersetzen', desc: 'Entpackt ZIP oder kopiert in Ordner' },
  { value: 'run_installer', label: 'Installer ausführen', desc: 'Führt MSI/EXE silent aus' },
  { value: 'run_script', label: 'Script ausführen', desc: 'Führt PS1/BAT Script aus' },
];

const POST_ACTIONS = [
  { value: 'none', label: 'Keine' },
  { value: 'restart_app', label: 'Anwendung neustarten' },
  { value: 'restart_service', label: 'Dienst neustarten' },
  { value: 'restart_pc', label: 'PC neustarten' },
];

const StatusIcon = ({ status }) => {
  switch (status) {
    case 'completed': return <Check size={16} weight="bold" className="text-emerald-400" />;
    case 'failed': return <Warning size={16} weight="bold" className="text-red-400" />;
    case 'downloading': case 'installing': return <ArrowClockwise size={16} weight="bold" className="text-sky-400 animate-spin" />;
    case 'partial': return <Warning size={16} weight="bold" className="text-amber-400" />;
    case 'cancelled': return <X size={16} weight="bold" className="text-zinc-500" />;
    default: return <Clock size={16} className="text-zinc-500" />;
  }
};

const StatusLabel = ({ status }) => {
  const map = {
    pending: 'Ausstehend', in_progress: 'Läuft', completed: 'Abgeschlossen',
    partial: 'Teilweise', failed: 'Fehlgeschlagen', cancelled: 'Abgebrochen',
    downloading: 'Download...', installing: 'Installiert...',
  };
  return <span className="text-xs">{map[status] || status}</span>;
};

export default function DeploymentsPage() {
  const { user } = useAuth();
  const { lastMessage } = useWebSocket();
  const [tab, setTab] = useState('deployments'); // deployments | packages | create
  const [deployments, setDeployments] = useState([]);
  const [packages, setPackages] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDep, setSelectedDep] = useState(null);

  // Create form
  const [form, setForm] = useState({
    name: '', description: '', package_id: '', action: 'copy_file',
    target_path: '', post_action: 'none', post_action_target: '',
    target_device_ids: [], queue_for_offline: true,
  });
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);
  const fileInputRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [depRes, pkgRes, devRes] = await Promise.all([
        api.get('/api/deployments'),
        api.get('/api/deployments/packages'),
        api.get('/api/devices?per_page=500'),
      ]);
      setDeployments(depRes.data.deployments || []);
      setPackages(pkgRes.data.packages || []);
      setDevices(devRes.data.devices || []);
    } catch (e) { console.error('Fetch error:', e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Handle real-time deploy status updates
  useEffect(() => {
    if (lastMessage?.type === 'deploy_status') {
      setDeployments(prev => prev.map(d => {
        if (d.id !== lastMessage.deployment_id) return d;
        const updated = { ...d };
        updated.device_results = (updated.device_results || []).map(r =>
          r.device_id === lastMessage.device_id
            ? { ...r, status: lastMessage.status, progress: lastMessage.progress }
            : r
        );
        const completed = updated.device_results.filter(r => r.status === 'completed').length;
        const failed = updated.device_results.filter(r => r.status === 'failed').length;
        updated.completed_devices = completed;
        updated.failed_devices = failed;
        if (completed + failed >= updated.total_devices) {
          updated.status = failed === 0 ? 'completed' : (completed === 0 ? 'failed' : 'partial');
        }
        return updated;
      }));
      // Also update selectedDep if viewing
      if (selectedDep?.id === lastMessage.deployment_id) {
        setSelectedDep(prev => {
          if (!prev) return prev;
          const updated = { ...prev };
          updated.device_results = (updated.device_results || []).map(r =>
            r.device_id === lastMessage.device_id
              ? { ...r, status: lastMessage.status, progress: lastMessage.progress }
              : r
          );
          return updated;
        });
      }
    }
  }, [lastMessage, selectedDep?.id]);

  // Upload file
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', file.name);
      await api.post('/api/deployments/packages/upload?name=' + encodeURIComponent(file.name), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      fetchData();
    } catch (e) { alert('Upload fehlgeschlagen: ' + (e.response?.data?.detail || e.message)); }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Add URL package
  const [urlForm, setUrlForm] = useState({ name: '', source_url: '', filename: '' });
  const [showUrlForm, setShowUrlForm] = useState(false);
  const handleUrlPackage = async () => {
    if (!urlForm.name || !urlForm.source_url) return;
    try {
      await api.post('/api/deployments/packages/url', urlForm);
      setShowUrlForm(false);
      setUrlForm({ name: '', source_url: '', filename: '' });
      fetchData();
    } catch (e) { alert('Fehler: ' + (e.response?.data?.detail || e.message)); }
  };

  // Delete package
  const handleDeletePkg = async (id) => {
    if (!window.confirm('Paket wirklich löschen?')) return;
    await api.delete(`/api/deployments/packages/${id}`);
    fetchData();
  };

  // Toggle device selection
  const toggleDevice = (deviceId) => {
    setForm(f => ({
      ...f,
      target_device_ids: f.target_device_ids.includes(deviceId)
        ? f.target_device_ids.filter(d => d !== deviceId)
        : [...f.target_device_ids, deviceId],
    }));
  };
  const selectAllDevices = () => setForm(f => ({ ...f, target_device_ids: devices.map(d => d.id) }));
  const selectNone = () => setForm(f => ({ ...f, target_device_ids: [] }));

  // Create deployment
  const handleCreate = async () => {
    if (!form.name || !form.package_id || !form.target_path || form.target_device_ids.length === 0) {
      alert('Bitte alle Pflichtfelder ausfüllen');
      return;
    }
    setCreating(true);
    try {
      await api.post('/api/deployments', form);
      setTab('deployments');
      setForm({ name: '', description: '', package_id: '', action: 'copy_file', target_path: '', post_action: 'none', post_action_target: '', target_device_ids: [], queue_for_offline: true });
      fetchData();
    } catch (e) { alert('Fehler: ' + (e.response?.data?.detail || e.message)); }
    setCreating(false);
  };

  // View deployment detail
  const viewDeployment = async (depId) => {
    try {
      const res = await api.get(`/api/deployments/${depId}`);
      setSelectedDep(res.data);
    } catch (e) { console.error(e); }
  };

  // Retry
  const handleRetry = async (depId) => {
    await api.post(`/api/deployments/${depId}/retry`);
    viewDeployment(depId);
    fetchData();
  };

  // Cancel
  const handleCancel = async (depId) => {
    await api.post(`/api/deployments/${depId}/cancel`);
    fetchData();
    if (selectedDep?.id === depId) setSelectedDep(null);
  };

  if (loading) return <div className="p-8 text-center text-zinc-500">Laden...</div>;

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto" data-testid="deployments-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2"><Rocket size={24} weight="bold" /> Deployment Center</h1>
          <p className="text-sm text-zinc-500 mt-1">Dateien, Software und Updates an Remote-Geräte verteilen</p>
        </div>
        <button data-testid="new-deployment-btn" onClick={() => setTab('create')} className="flex items-center gap-2 px-4 py-2 bg-[#D50C2D] text-white text-sm font-medium rounded-lg hover:bg-[#b00a26] transition-colors">
          <Plus size={16} weight="bold" /> Neues Deployment
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-[#141416] rounded-lg p-1 w-fit" data-testid="deployment-tabs">
        {[['deployments', 'Deployments'], ['packages', 'Pakete'], ['create', 'Erstellen']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} className={`px-4 py-2 text-sm rounded-md transition-colors ${tab === key ? 'bg-[#1E1E21] text-white font-medium' : 'text-zinc-500 hover:text-zinc-300'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Detail Modal */}
      {selectedDep && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setSelectedDep(null)}>
          <div className="bg-[#141416] border border-white/10 rounded-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="deployment-detail-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <div>
                <h2 className="text-white font-bold">{selectedDep.name}</h2>
                <p className="text-xs text-zinc-500 mt-1">{selectedDep.package_filename} → {selectedDep.target_path}</p>
              </div>
              <button onClick={() => setSelectedDep(null)} className="text-zinc-500 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <StatusIcon status={selectedDep.status} />
                <StatusLabel status={selectedDep.status} />
                <span className="text-xs text-zinc-600 ml-auto">{selectedDep.completed_devices}/{selectedDep.total_devices} fertig</span>
                {selectedDep.status !== 'completed' && selectedDep.status !== 'cancelled' && (
                  <button onClick={() => handleRetry(selectedDep.id)} className="text-xs px-3 py-1 bg-sky-500/20 text-sky-400 rounded hover:bg-sky-500/30" data-testid="retry-btn">Retry</button>
                )}
              </div>
              <div className="space-y-2">
                {(selectedDep.device_results || []).map(r => (
                  <div key={r.device_id} className="flex items-center gap-3 px-3 py-2 bg-[#0A0A0B] rounded-lg text-sm">
                    <StatusIcon status={r.status} />
                    <span className="text-white font-medium flex-1">{r.computer_name || r.device_id.slice(0, 8)}</span>
                    {r.status === 'downloading' && <div className="w-20 h-1.5 bg-zinc-800 rounded-full overflow-hidden"><div className="h-full bg-sky-500 rounded-full transition-all" style={{ width: `${r.progress}%` }} /></div>}
                    <StatusLabel status={r.status} />
                    {r.error && <span className="text-xs text-red-400 max-w-[200px] truncate" title={r.error}>{r.error}</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB: Deployments List */}
      {tab === 'deployments' && (
        <div className="space-y-3" data-testid="deployments-list">
          {deployments.length === 0 ? (
            <div className="text-center py-16 text-zinc-600">
              <Rocket size={48} className="mx-auto mb-3 opacity-30" />
              <p>Noch keine Deployments erstellt</p>
            </div>
          ) : deployments.map(dep => (
            <div key={dep.id} className="bg-[#141416] border border-white/5 rounded-lg p-4 hover:border-white/10 transition-colors cursor-pointer" onClick={() => viewDeployment(dep.id)} data-testid={`deployment-${dep.id}`}>
              <div className="flex items-center gap-3">
                <StatusIcon status={dep.status} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-white font-medium text-sm truncate">{dep.name}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded bg-white/5 text-zinc-400">{ACTIONS.find(a => a.value === dep.action)?.label || dep.action}</span>
                  </div>
                  <p className="text-xs text-zinc-600 mt-0.5 truncate">{dep.package_filename} → {dep.target_path}</p>
                </div>
                <div className="text-right shrink-0">
                  <div className="flex items-center gap-1 text-xs">
                    <span className="text-emerald-400">{dep.completed_devices}</span>
                    <span className="text-zinc-600">/</span>
                    <span className="text-zinc-400">{dep.total_devices}</span>
                    {dep.failed_devices > 0 && <span className="text-red-400 ml-1">({dep.failed_devices} err)</span>}
                  </div>
                  <p className="text-[10px] text-zinc-600 mt-0.5">{new Date(dep.created_at).toLocaleString('de')}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  {dep.status !== 'completed' && dep.status !== 'cancelled' && (
                    <button onClick={e => { e.stopPropagation(); handleCancel(dep.id); }} className="p-1.5 text-zinc-600 hover:text-red-400" title="Abbrechen"><X size={14} /></button>
                  )}
                </div>
              </div>
              {dep.status === 'in_progress' && (
                <div className="mt-3 w-full h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                  <div className="h-full bg-sky-500 rounded-full transition-all" style={{ width: `${dep.total_devices > 0 ? Math.round((dep.completed_devices / dep.total_devices) * 100) : 0}%` }} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* TAB: Packages */}
      {tab === 'packages' && (
        <div data-testid="packages-tab">
          <div className="flex gap-2 mb-4">
            <label className="flex items-center gap-2 px-4 py-2 bg-[#1E1E21] text-white text-sm rounded-lg cursor-pointer hover:bg-[#252528] transition-colors">
              <Upload size={16} /> {uploading ? 'Uploading...' : 'Datei hochladen'}
              <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} disabled={uploading} data-testid="file-upload-input" />
            </label>
            <button onClick={() => setShowUrlForm(!showUrlForm)} className="flex items-center gap-2 px-4 py-2 bg-[#1E1E21] text-white text-sm rounded-lg hover:bg-[#252528] transition-colors" data-testid="add-url-btn">
              <LinkIcon size={16} /> URL-Paket
            </button>
          </div>

          {showUrlForm && (
            <div className="bg-[#141416] border border-white/10 rounded-lg p-4 mb-4 space-y-3" data-testid="url-form">
              <input className="w-full bg-[#0A0A0B] border border-white/10 rounded px-3 py-2 text-sm text-white" placeholder="Name (z.B. Regula DB Update)" value={urlForm.name} onChange={e => setUrlForm(f => ({ ...f, name: e.target.value }))} />
              <input className="w-full bg-[#0A0A0B] border border-white/10 rounded px-3 py-2 text-sm text-white" placeholder="Download-URL" value={urlForm.source_url} onChange={e => setUrlForm(f => ({ ...f, source_url: e.target.value }))} />
              <input className="w-full bg-[#0A0A0B] border border-white/10 rounded px-3 py-2 text-sm text-white" placeholder="Dateiname (z.B. db.dat)" value={urlForm.filename} onChange={e => setUrlForm(f => ({ ...f, filename: e.target.value }))} />
              <div className="flex gap-2">
                <button onClick={handleUrlPackage} className="px-4 py-2 bg-[#D50C2D] text-white text-sm rounded hover:bg-[#b00a26]">Speichern</button>
                <button onClick={() => setShowUrlForm(false)} className="px-4 py-2 text-zinc-500 text-sm hover:text-white">Abbrechen</button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {packages.length === 0 ? (
              <div className="text-center py-12 text-zinc-600"><Folder size={48} className="mx-auto mb-3 opacity-30" /><p>Keine Pakete vorhanden</p></div>
            ) : packages.map(pkg => (
              <div key={pkg.id} className="flex items-center gap-3 bg-[#141416] border border-white/5 rounded-lg px-4 py-3" data-testid={`package-${pkg.id}`}>
                <div className={`p-2 rounded ${pkg.source_type === 'url' ? 'bg-sky-500/10 text-sky-400' : 'bg-emerald-500/10 text-emerald-400'}`}>
                  {pkg.source_type === 'url' ? <LinkIcon size={18} /> : <Download size={18} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{pkg.name}</p>
                  <p className="text-xs text-zinc-600 truncate">
                    {pkg.source_type === 'url' ? pkg.source_url : `${pkg.filename} (${(pkg.file_size / 1024 / 1024).toFixed(1)} MB)`}
                  </p>
                </div>
                <span className="text-[10px] text-zinc-600 shrink-0">{new Date(pkg.created_at).toLocaleDateString('de')}</span>
                <button onClick={() => handleDeletePkg(pkg.id)} className="p-1.5 text-zinc-600 hover:text-red-400" title="Löschen"><Trash size={14} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB: Create Deployment */}
      {tab === 'create' && (
        <div className="max-w-2xl space-y-5" data-testid="create-deployment-form">
          <div className="bg-[#141416] border border-white/5 rounded-lg p-5 space-y-4">
            <h3 className="text-white font-bold text-sm">Deployment-Details</h3>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Name *</label>
              <input className="w-full bg-[#0A0A0B] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" placeholder="z.B. Regula DB Update Q2-2026" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="deploy-name" />
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Beschreibung</label>
              <input className="w-full bg-[#0A0A0B] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" placeholder="Optional" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>
          </div>

          <div className="bg-[#141416] border border-white/5 rounded-lg p-5 space-y-4">
            <h3 className="text-white font-bold text-sm">Paket & Aktion</h3>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Paket auswählen *</label>
              <select className="w-full bg-[#0A0A0B] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" value={form.package_id} onChange={e => setForm(f => ({ ...f, package_id: e.target.value }))} data-testid="deploy-package">
                <option value="">-- Paket wählen --</option>
                {packages.map(p => <option key={p.id} value={p.id}>{p.name} ({p.source_type === 'url' ? 'URL' : `${(p.file_size/1024/1024).toFixed(1)}MB`})</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Aktion *</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {ACTIONS.map(a => (
                  <button key={a.value} onClick={() => setForm(f => ({ ...f, action: a.value }))}
                    className={`text-left px-3 py-2 rounded-lg border text-sm transition-colors ${form.action === a.value ? 'border-[#D50C2D] bg-[#D50C2D]/10 text-white' : 'border-white/5 text-zinc-400 hover:border-white/20'}`}>
                    <span className="font-medium">{a.label}</span>
                    <p className="text-[10px] text-zinc-600 mt-0.5">{a.desc}</p>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Zielpfad auf Remote-Gerät *</label>
              <input className="w-full bg-[#0A0A0B] border border-white/10 rounded-lg px-3 py-2 text-sm text-white font-mono" placeholder="C:\Firma\Dokumente\" value={form.target_path} onChange={e => setForm(f => ({ ...f, target_path: e.target.value }))} data-testid="deploy-target-path" />
            </div>
          </div>

          <div className="bg-[#141416] border border-white/5 rounded-lg p-5 space-y-4">
            <h3 className="text-white font-bold text-sm">Nach dem Deployment</h3>
            <div className="flex flex-wrap gap-2">
              {POST_ACTIONS.map(pa => (
                <button key={pa.value} onClick={() => setForm(f => ({ ...f, post_action: pa.value }))}
                  className={`px-3 py-1.5 rounded text-xs transition-colors ${form.post_action === pa.value ? 'bg-[#D50C2D]/20 text-[#D50C2D] border border-[#D50C2D]/30' : 'bg-white/5 text-zinc-400 border border-transparent hover:bg-white/10'}`}>
                  {pa.label}
                </button>
              ))}
            </div>
            {(form.post_action === 'restart_app' || form.post_action === 'restart_service') && (
              <input className="w-full bg-[#0A0A0B] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" placeholder={form.post_action === 'restart_app' ? 'Prozessname (z.B. regula.exe)' : 'Dienstname (z.B. RegulaService)'} value={form.post_action_target} onChange={e => setForm(f => ({ ...f, post_action_target: e.target.value }))} />
            )}
            <label className="flex items-center gap-2 text-sm text-zinc-400">
              <input type="checkbox" checked={form.queue_for_offline} onChange={e => setForm(f => ({ ...f, queue_for_offline: e.target.checked }))} className="rounded bg-[#0A0A0B] border-white/20" />
              Offline-Geräte automatisch nachholen (bei Reconnect)
            </label>
          </div>

          <div className="bg-[#141416] border border-white/5 rounded-lg p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-bold text-sm">Zielgeräte * <span className="font-normal text-zinc-500">({form.target_device_ids.length} ausgewählt)</span></h3>
              <div className="flex gap-2">
                <button onClick={selectAllDevices} className="text-xs text-sky-400 hover:text-sky-300">Alle</button>
                <button onClick={selectNone} className="text-xs text-zinc-500 hover:text-zinc-300">Keine</button>
              </div>
            </div>
            <div className="max-h-60 overflow-y-auto space-y-1">
              {devices.map(d => (
                <label key={d.id} className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${form.target_device_ids.includes(d.id) ? 'bg-[#D50C2D]/10 border border-[#D50C2D]/20' : 'hover:bg-white/5 border border-transparent'}`}>
                  <input type="checkbox" checked={form.target_device_ids.includes(d.id)} onChange={() => toggleDevice(d.id)} className="rounded" />
                  <Desktop size={14} className={d.is_online ? 'text-emerald-400' : 'text-zinc-600'} />
                  <span className="text-sm text-white">{d.computer_name}</span>
                  <span className={`text-[10px] ml-auto ${d.is_online ? 'text-emerald-400' : 'text-zinc-600'}`}>{d.is_online ? 'Online' : 'Offline'}</span>
                  {d.agent_type === 'node' && <span className="text-[9px] px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-400 border border-sky-500/30">v2</span>}
                </label>
              ))}
            </div>
          </div>

          <button onClick={handleCreate} disabled={creating} className="w-full py-3 bg-[#D50C2D] text-white font-bold rounded-lg hover:bg-[#b00a26] transition-colors disabled:opacity-50" data-testid="start-deployment-btn">
            {creating ? 'Wird erstellt...' : `Deployment starten (${form.target_device_ids.length} Geräte)`}
          </button>
        </div>
      )}
    </div>
  );
}
